/**
 * @fileoverview Main drag & drop interface for cost center planning
 * Provides two-column layout for available cost centers and ordered sequence
 */

import React, { useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { CentroCosto } from '../types/collections/CentroCosto';
import { CentroCostoPeso } from '../types/collections/CentroCostoPeso';

// Import child components (will be created in next subtasks)
import { CentroCostoList } from './CentroCostoList';
import { ProcesoOrderList } from './ProcesoOrderList';

/**
 * Props for DragDropInterface component
 */
export interface DragDropInterfaceProps {
  /** Available cost centers that can be dragged */
  centrosCosto: CentroCosto[];
  /** Current sequence of cost centers with weights */
  secuencia: CentroCostoPeso[];
  /** Callback when sequence changes */
  onSecuenciaChange: (newSecuencia: CentroCostoPeso[]) => void;
  /** Callback when items are reordered within sequence */
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Callback when weight of an item changes */
  onWeightChange?: (index: number, peso: number) => void;
  /** Whether the interface is in read-only mode */
  readOnly?: boolean;
}

/**
 * Main drag & drop interface component
 * Provides two-column layout for cost center planning
 */
export const DragDropInterface: React.FC<DragDropInterfaceProps> = ({
  centrosCosto,
  secuencia,
  onSecuenciaChange,
  onReorder,
  onWeightChange,
  readOnly = false
}) => {
  /**
   * Handle adding a cost center to the sequence
   * @param centroCostoId - ID of the cost center to add
   */
  const handleAddToSequence = useCallback((centroCostoId: string) => {
    if (readOnly) return;
    
    const centroEncontrado = centrosCosto.find(c => c.id === centroCostoId);
    const nuevoCentroPeso: CentroCostoPeso = {
      id: `temp_${Date.now()}`, // Temporary ID for new items
      centro_costo: centroCostoId,
      peso: 0,
      activo: true,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      collectionId: 'temp',
      collectionName: 'centro_costo_peso',
      expand: centroEncontrado ? {
        centro_costo: {
          id: centroEncontrado.id,
          nombre: centroEncontrado.nombre || 'Sin nombre',
          activo: centroEncontrado.activo,
          maquina: centroEncontrado.maquina,
          encargado: centroEncontrado.encargado,
          proceso: centroEncontrado.proceso,
          created: centroEncontrado.created,
          updated: centroEncontrado.updated,
          collectionId: centroEncontrado.collectionId,
          collectionName: centroEncontrado.collectionName,
          // Include expanded data if available
          expand: centroEncontrado.expand || {}
        }
      } : undefined
    };
    
    const newSecuencia = [...secuencia, nuevoCentroPeso];
    onSecuenciaChange(newSecuencia);
  }, [secuencia, onSecuenciaChange, readOnly, centrosCosto]);

  /**
   * Handle removing a cost center from the sequence
   * @param index - Index of the cost center to remove
   */
  const handleRemoveFromSequence = useCallback((index: number) => {
    if (readOnly) return;
    
    const newSecuencia = secuencia.filter((_, i) => i !== index);
    onSecuenciaChange(newSecuencia);
  }, [secuencia, onSecuenciaChange, readOnly]);

  /**
   * Handle reordering within the sequence
   * @param fromIndex - Source index
   * @param toIndex - Destination index
   */
  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (readOnly) return;
    
    onReorder(fromIndex, toIndex);
  }, [onReorder, readOnly]);

  /**
   * Handle weight change for a cost center in the sequence
   * @param index - Index of the cost center
   * @param peso - New weight value
   */
  const handleWeightChange = useCallback((index: number, peso: number) => {
    if (readOnly) return;
    
    if (onWeightChange) {
      onWeightChange(index, peso);
    }
  }, [onWeightChange, readOnly]);

  /**
   * Get available cost centers (not in current sequence)
   */
  const centrosDisponibles = centrosCosto.filter(
    centro => !secuencia.some(item => item && item.centro_costo === centro.id)
  );

  /**
   * Get cost centers in current sequence with full data
   */
  const secuenciaCompleta = secuencia
    .filter(item => item != null) // Filter out null/undefined items
    .map(item => {
      // If the item doesn't have expanded data, try to find it
      if (!item.expand?.centro_costo && item.centro_costo) {
        const centro = centrosCosto.find(c => c.id === item.centro_costo);
        return {
          ...item,
          expand: {
            centro_costo: centro
          }
        };
      }
      return item;
    })
    .filter(Boolean) as CentroCostoPeso[];

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="drag-drop-interface">
        <Row>
          {/* Left Column: Available Cost Centers */}
          <Col md={6}>
            <Card className="h-100">
              <CardBody>
                <CardTitle className="h5 mb-3">
                  <i className="fas fa-list me-2"></i>
                  Centros de Costo Disponibles
                </CardTitle>
                <CentroCostoList
                  centrosCosto={centrosDisponibles}
                  onDragStart={handleAddToSequence}
                  readOnly={readOnly}
                />
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Ordered Sequence */}
          <Col md={6}>
            <Card className="h-100">
              <CardBody>
                <CardTitle className="h5 mb-3">
                  <i className="fas fa-sort-numeric-down me-2"></i>
                  Secuencia de Procesos
                </CardTitle>
                <ProcesoOrderList
                  secuencia={secuenciaCompleta}
                  onRemove={handleRemoveFromSequence}
                  onReorder={handleReorder}
                  onAddToSequence={handleAddToSequence}
                  onWeightChange={handleWeightChange}
                  readOnly={readOnly}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Instructions */}
        {!readOnly && (
          <Row className="mt-3">
            <Col>
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Instrucciones:</strong> Arrastra los centros de costo desde la columna izquierda 
                hacia la columna derecha para crear la secuencia de procesos. Puedes reordenar los elementos 
                en la secuencia arrastrándolos a diferentes posiciones.
              </div>
            </Col>
          </Row>
        )}

        {/* Sequence Summary */}
        {secuencia && Array.isArray(secuencia) && secuencia.length > 0 && (
          <Row className="mt-3">
            <Col>
              {(() => {
                const totalPeso = secuencia
                  .filter(item => item != null) // Filter out null/undefined items
                  .reduce((sum, item) => sum + (item.peso || 0), 0);
                
                // Check if any item has peso 0
                const hasZeroWeight = secuencia
                  .filter(item => item != null)
                  .some(item => (item.peso || 0) === 0);
                
                // Determine alert type based on validation state
                const alertType = hasZeroWeight || totalPeso > 100 ? 'warning' : 'success';
                const iconClass = hasZeroWeight || totalPeso > 100 ? 'fa-exclamation-triangle' : 'fa-check-circle';
                
                return (
                  <div className={`alert alert-${alertType}`}>
                    <i className={`fas ${iconClass} me-2`}></i>
                    <strong>Secuencia Actual:</strong> {secuencia.length} proceso(s) configurado(s)
                    {secuencia.length === 1 && ' (mínimo requerido)'}
                    {secuencia.length > 1 && ` (${secuencia.length} pasos)`}
                    <span className="ms-2">
                      - Peso total: <strong>{totalPeso}%</strong>
                      {totalPeso > 100 && <span className="text-danger ms-1">(excede 100%)</span>}
                      {hasZeroWeight && <span className="text-warning ms-1">(algunos procesos tienen peso 0)</span>}
                    </span>
                  </div>
                );
              })()}
            </Col>
          </Row>
        )}
      </div>
    </DndProvider>
  );
};
